/**
 * @file A simple event emitter utility.
 * @module EventEmitter
 */

export class EventEmitter {
    constructor() {
        this.events = {};
    }

    /**
     * Registers an event listener.
     * @param {string} eventName - The name of the event to listen for.
     * @param {function} listener - The callback function to execute when the event is emitted.
     */
    on(eventName, listener) {
        if (!this.events[eventName]) {
            this.events[eventName] = [];
        }
        this.events[eventName].push(listener);
    }

    /**
     * Emits an event, calling all registered listeners for that event.
     * @param {string} eventName - The name of the event to emit.
     * @param {...any} args - Arguments to pass to the listeners.
     */
    emit(eventName, ...args) {
        if (this.events[eventName]) {
            this.events[eventName].forEach(listener => {
                listener(...args);
            });
        }
    }

    /**
     * Registers a one-time event listener.
     * @param {string} eventName - The name of the event to listen for.
     * @param {function} listener - The callback function to execute once when the event is emitted.
     */
    once(eventName, listener) {
        const onceWrapper = (...args) => {
            listener(...args);
            this.off(eventName, onceWrapper);
        };
        this.on(eventName, onceWrapper);
    }

    /**
     * Removes an event listener.
     * @param {string} eventName - The name of the event.
     * @param {function} listener - The listener function to remove.
     */
    off(eventName, listener) {
        if (this.events[eventName]) {
            this.events[eventName] = this.events[eventName].filter(l => l !== listener);
        }
    }
}
